import React, { useRef, useEffect, useState } from "react"
import DropDown from "aldoo-ra/Dropdown"
import Spinner from "aldoo-ra/Spinner"
import Tooltip from "aldoo-ra/Tooltip"
import { getFieldByLocalizationKey } from "../get-cms-info-by-key"
import { getFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import SnowflakeIcon, { getFreezeState } from "../snowflake-icon"
import LocalizationAIRewriteDialog from "../localization-ai-rewrite-dialog"

/**
 * TextItemEditor Component
 *
 * @param {Object} props
 * @param {Object} translations - The translation data for the languages
 * @param {Function} handleContentChange - Function to handle content change for the selected language
 * @param {String} selectedLang - The currently selected language code
 * @param {String} primaryLanguage - The primary language code
 * @param {Object} controller - The controller object containing necessary methods and data for translations
 */
export default function TextItemEditor({
  localizationKey,
  translations,
  handleContentChange,
  selectedLang,
  primaryLanguage,
  controller,
  parentObject,
  item,
}) {
  const [showPreview, setShowPreview] = useState(false)
  const [aiRewriteOpen, setAiRewriteOpen] = useState(false)
  //get the field by name from the fieldInfo
  //contains the field information such as id, name, type, etc.
  let field = getFieldByLocalizationKey(localizationKey)

  //set a default field if not found
  if (!field)
    field = { type: "Text", multiple: false, default: "", multiline: true }

  //not yet loaded
  if (!field) return <Spinner />

  const FieldEditor = getFieldEditor(field)

  const defaultValue = field.multiple ? [] : field.default || ""
  //get the primary language value and fieldInfo
  const primaryValue = translations[primaryLanguage]?.content || defaultValue
  const value = translations[selectedLang]?.content || defaultValue

  return (
    <div className="min-h-[500px] flex flex-col">
      {aiRewriteOpen && (
        <LocalizationAIRewriteDialog
          originalText={typeof value === 'string' ? value : ""}
          onAccept={(rewritten) => {
            if (!showPreview) handleContentChange(rewritten)
          }}
          onClose={() => setAiRewriteOpen(false)}
        />
      )}
      <div className="flex justify-between flex-col gap-2">
        <div className="flex flex-row justify-between">
          {/* Display the selected language name */}
          <span className="font-semibold text-admin_text text-lg">
            {controller.getLanguageName(selectedLang)}
          </span>
          {/* Auto-translate options dropdown and preview toggle */}
          <div className="flex flex-row items-center gap-3 w-1/2">
            <LoadingOverlay
              title="Translating"
              message="Please wait while we translate the content"
              loading={controller.isAutoTranslating}
            />
            <DropDown
              className="w-full"
              variant="admin"
              isMenu={true}
              placeholder="Auto Translate"
              options={
                //if the selected language is the primary language, do not show the primary language in the auto-translate options
                selectedLang === primaryLanguage
                  ? controller.autoTranslateOptions.filter(
                      (option) => option.value !== "key"
                    )
                  : controller.autoTranslateOptions
              }
              onChange={(item) => {
                controller.setSelectedAutoTranslateOption(item.value)
              }}
              sortBy="index"
              sortDirection="desc"
              menuPosition="bottom"
              onItemRender={(item) => item.name}
            />

            {/* AI Rewrite button */}
            {!showPreview && (
              <Tooltip content="AI rewrite this value" variant="admin">
                <button
                  onClick={() => setAiRewriteOpen(true)}
                  className="w-7 h-7 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors bg-transparent text-admin_primary flex-shrink-0"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 0 1 3 3v1h.5a1.5 1.5 0 0 1 0 3H19v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1h-.5a1.5 1.5 0 0 1 0-3H5v-1a3 3 0 0 1 3-3h3V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-2 9a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm4 0a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm-5 5v1h6v-1H9z"/>
                  </svg>
                </button>
              </Tooltip>
            )}

            {/* Preview toggle - only for non-primary languages and when primary value exists */}
            {selectedLang !== primaryLanguage && primaryValue && (
              <Tooltip
                content={showPreview
                  ? `View in ${controller?.getLanguageName(selectedLang) || 'current language'}`
                  : `Preview in ${controller?.getLanguageName(primaryLanguage) || 'English'}`}
                variant="admin"
              >
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`w-8 h-8 border border-admin_bordered rounded-full flex items-center justify-center cursor-pointer hover:bg-admin_neutral_1 transition-colors flex-shrink-0 ${
                    showPreview
                      ? 'bg-admin_primary border-admin_primary'
                      : 'bg-transparent'
                  }`}
                >
                  {showPreview ? (
                    <span className="text-xs text-admin_text_inv">✓</span>
                  ) : (
                    <span className="text-xs text-admin_text">📖</span>
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="w-full flex-1 flex items-stretch mt-2">
        {FieldEditor && (
          <div className="w-full">
            <FieldEditor
              parentObject={parentObject}
              editButtonTitle={`Edit ${controller.getLanguageName(selectedLang)}`}
              field={field}
              value={showPreview ? primaryValue : value}
              readOnly={showPreview}
              variant={showPreview ? "admin_preview" : "admin"}
              placeholder={showPreview ? `Preview: ${controller?.getLanguageName(primaryLanguage) || 'English'}` : undefined}
              onChange={showPreview ? undefined : (changed) => {
                if (changed === undefined) return
                handleContentChange(changed)
              }}
            />
          </div>
        )}
      </div>

    </div>
  )
}
